import { Box, Typography } from '@mui/material';
import { Image } from '@shopify/hydrogen';
import type { FC } from 'react';

import { SocialEmailIcon, SocialLinkedinIcon, SocialPhoneIcon } from '~/components/atoms/Icon';
import { Link } from '~/components/atoms/Link';
import type { PeopleProps } from '~/types/sanity/components';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';

type PeopleItemProps = NonNullable<PeopleProps['people']>[0];

export const PeopleItem: FC<PeopleItemProps> = ({
  photo: { asset: photo },
  name,
  textLine1,
  textLine2,
  phoneNumber,
  linkedIn,
  email,
}) => {
  const { url, metadata } = photo ?? {};
  const { width, height, aspectRatio } = metadata?.dimensions ?? {};

  // slugify name for test id
  const slug = name.replace(/\W+/g, '-').toLowerCase();
  const getCompoundTestId = getCompoundTestIdGenerator('peopleItem');

  return (
    <Box
      display="flex"
      gap={{ xs: 3, md: 2 }}
      flexDirection={{
        xs: 'row',
        md: 'column',
      }}
      data-testid={getCompoundTestId(slug)}
    >
      {/* Photo */}
      {url && (
        <Box width="100%" maxWidth={{ xs: '140px', md: '100%' }} flexShrink={0}>
          <Box
            position="relative"
            display="flex"
            flexDirection="column"
            borderRadius="50%"
            overflow="hidden"
            sx={{ aspectRatio: aspectRatio || 1 }}
          >
            <Image
              src={url}
              {...(width && { width })}
              {...(height && { height })}
              alt={`Photo of ${name}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        </Box>
      )}

      {/* Details & Contact Information */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems={{ md: 'center' }}
        gap={{ xs: 2, md: 3 }}
        textAlign={{ md: 'center' }}
      >
        {/* Details */}
        <Box>
          <Typography variant="h6" component="h3" fontSize={{ md: 18 }}>
            {name}
          </Typography>

          {/* Text lines */}
          {[textLine1, textLine2].map((text, i) => {
            if (!text) {
              return null;
            }
            const id = text.replace(/\W+/g, '-').toLowerCase().substring(0, 5);
            const key = `${id}_${i}`;

            return (
              <Typography key={key} fontSize={{ xs: 18, md: 16 }}>
                {text}
              </Typography>
            );
          })}
        </Box>

        {/* Contact Information */}
        <Box display="flex" gap={3}>
          {/* Phone Number */}
          {phoneNumber && (
            <Link to={`tel:${phoneNumber}`} target="_blank" color="secondary.main">
              <SocialPhoneIcon viewBox="0 0 24 24" width={34} height={34} />
            </Link>
          )}

          {/* LinkedIn */}
          {linkedIn && (
            <Link to={linkedIn} target="_blank" color="secondary.main">
              <SocialLinkedinIcon viewBox="0 0 34 34" width={34} height={34} />
            </Link>
          )}

          {/* Email */}
          {email && (
            <Link to={`mailto:${email}`} target="_blank" color="secondary.main">
              <SocialEmailIcon viewBox="0 0 24 24" width={34} height={34} />
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
};
